import { filter, map } from 'rxjs';

import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';

import { ThemeService } from '@echospaces-web-monorepo/es-shared-theme';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import {
  BackgroundGlowComponent,
  NavItemComponent,
  SidebarComponent,
  SidebarSectionComponent,
  ToastOutletComponent,
  type EsnIconName,
} from '@echospaces-web-monorepo/es-ui-next';

import { ACTIVE_COMPANY } from '../active-company.token';
import { PortalTourComponent } from '../tour/portal-tour.component';
import { PortalTourService } from '../tour/portal-tour.service';

interface NavEntry {
  readonly label: string;
  readonly icon: EsnIconName;
  readonly route: string;
}

const MAIN_NAV: readonly NavEntry[] = [
  { label: 'Dashboard', icon: 'grid-view', route: '/dashboard' },
  { label: 'Islands', icon: 'islands', route: '/islands' },
  { label: 'Content', icon: 'content', route: '/content' },
  { label: 'Link Content', icon: 'link', route: '/placeholders-links' },
];

const ADMIN_NAV: readonly NavEntry[] = [
  { label: 'Client accounts', icon: 'users', route: '/users' },
  { label: 'Admin console', icon: 'settings', route: '/admin' },
];

const SUPER_ADMIN_ROLE = 'super-admin';

/**
 * The portal frame from the Figma templates: navigation rail (a drawer on narrow screens),
 * the account row that opens Account Settings, help + tour entries and the theme switch,
 * all on the background glow. Each page renders its own page header.
 */
@Component({
  selector: 'es-shell',
  imports: [
    RouterOutlet,
    RouterLink,
    BackgroundGlowComponent,
    NavItemComponent,
    SidebarComponent,
    SidebarSectionComponent,
    ToastOutletComponent,
    PortalTourComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  protected readonly auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);
  protected readonly tour = inject(PortalTourService);
  private readonly router = inject(Router);

  /**
   * The company this BFF is bound to — badged under the account row. Null when unknown.
   */
  protected readonly company = inject(ACTIVE_COMPANY, { optional: true });

  protected readonly mainNav = MAIN_NAV;

  /**
   * Client accounts and the admin console are listed only for the `super-admin` realm role.
   */
  protected readonly adminNav = computed(() =>
    this.auth.user()?.roles.includes(SUPER_ADMIN_ROLE) ? ADMIN_NAV : []);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly userName = computed(() => {
    const user = this.auth.user();
    if (!user) return null;
    const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return full || user.username;
  });

  protected isActive(route: string): boolean {
    return this.url().startsWith(route);
  }

  protected setLightTheme(light: boolean): void {
    this.theme.setTheme(light ? 'light' : 'dark');
  }

  protected openAccountSettings(): void {
    void this.router.navigate(['/settings']);
  }
}
